import { reaction } from "mobx";
import { cast, flow, types } from "mobx-state-tree";

import { FeatureFlagResponse } from "../../api-models/response/FeatureFlagResponse";
import { endpoints } from "../api/ApiEndpoints";
import { api } from "../api/ApiService";
import { organizationSubscriptionService } from "../organization/OrganizationSubscription";
import { StorageModel } from "../storage/StorageModel";
import { FeatureFlag } from "./FeatureFlag";
import { Flags } from "../../generated/FeatureFlags";

const FeatureFlagsModel = types.model({
  featureFlags: types.optional(types.array(types.frozen<FeatureFlag>()), []),
  flagsWereChanged: false,
});

const FeatureFlagsStorageModel = types
  .compose(FeatureFlagsModel, StorageModel)
  .named("FeatureFlagsStorageModel")
  .actions((self) => {
    return {
      mapResponse(featureFlags: FeatureFlagResponse[]) {
        self.featureFlags = cast(featureFlags.map((x) => new FeatureFlag(x)));
      },
      loadFeatureFlagsOnDemand: flow(function* () {
        if (!self.featureFlags.length) {
          self.flagsWereChanged = false;
          yield self.load();
        }
      }),
      isFeatureEnabled(featureFlag: Flags) {
        if (!self.featureFlags.length) return false;
        return self.featureFlags.find((x) => x.name === featureFlag)?.isEnabled ?? false;
      },
      resetFeatureFlags() {
        self.flagsWereChanged = true;
        self.featureFlags = cast([]);
      },
    };
  })
  .views((self) => ({
    get endpoint() {
      const endpoint = endpoints.get("feature_flags");
      return endpoint;
    },
    get isLoading() {
      return !self.featureFlags.length && !self.flagsWereChanged;
    },
  }));

export const featureFlagsStorage = FeatureFlagsStorageModel.create({ autoLoad: false }, { api });

reaction(
  () => organizationSubscriptionService.key,
  async () => {
    await featureFlagsStorage.loadFeatureFlagsOnDemand();
  }
);
